import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { BaseLoader, ErrorMessage, SubmitButton } from '@retainerclub/common';
import { CREATE_USER } from '@retainerclub/customer-api';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { initialValues, schema } from './helpers';

function FamilyUsersModalWrapper({ handleCloseModal, children }) {
  const [mutationErrors, setMutationErrors] = useState(null);

  const [create, { error, loading }] = useMutation(CREATE_USER);

  const memoizedInitialValues = useMemo(() => initialValues(), []);

  const {
    register,
    handleSubmit,
    formState: { isLoading, isSubmitting, isValid },
    formState,
    getValues,
    setValue,
    control,
    ...rest
  } = useForm({
    resolver: yupResolver(schema()),
    defaultValues: memoizedInitialValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  async function submitRecord(values) {
    try {
      const params = {
        variables: {
          input: {
            input: { ...values },
            clientMutationId: crypto.randomUUID(),
          },
        },
      };

      const { data } = await create(params);

      const mErrors = data.createUser.errorMessages;

      if (mErrors.length > 0) {
        setMutationErrors(mErrors);
      } else {
        setMutationErrors(null);
        handleCloseModal();
      }
    } catch (err) {
      console.error(err);
    }
  }

  if (loading) {
    return <BaseLoader title="User" />;
  }

  return (
    <FormProvider
      {...rest}
      control={control}
      register={register}
      handleSubmit={handleSubmit}
      formState={formState}
      getValues={getValues}
      setValue={setValue}
    >
      <form onSubmit={handleSubmit(submitRecord)} noValidate>
        <div className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-6">
          <div className="col-span-6 grid grid-cols-1 gap-4 sm:grid-cols-6 md:col-span-6">
            {children}

            <div className="col-span-full">
              {error && (
                <div className="col-span-full mt-4">
                  <ErrorMessage error={error} />
                </div>
              )}

              {mutationErrors && (
                <div className="col-span-full mt-4">
                  <ErrorMessage errorMessages={mutationErrors} />
                </div>
              )}
              <div className="col-span-full mt-4 flex items-center justify-end gap-x-4">
                <SubmitButton
                  isLoading={isLoading || loading}
                  isSubmitting={isSubmitting}
                  isValid={isValid}
                  label="Invite a new account holder"
                  full
                  notRounded
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}

FamilyUsersModalWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  handleCloseModal: PropTypes.func.isRequired,
};

export default FamilyUsersModalWrapper;
